/**
 * BrandingTab
 *
 * Settings tab for managing tenant branding: company name, logo, colors, and typography.
 * Requires `settings.manage_branding` permission.
 */

import { useState, useEffect, useCallback, type ChangeEvent, type SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { describeApiError } from '@/lib/describeApiError';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { api } from '@/lib/api';
import { useNotify } from '@/hooks/useNotify';
import { useBranding } from '@/hooks/useBranding';
import { useAuthStore } from '@/stores/authStore';
import { useBrandingStore } from '@/stores/brandingStore';
import type {
  BrandingConfig,
  BrandingLogoConfig,
  BrandingColorConfig,
  TenantBrandingResponse,
} from '@/config/branding';
import { isValidColor } from '@/utils/themeBuilder';
import { EmailDomainSection } from './EmailDomainSection';

const BRANDING_FILE_ACCEPT = 'image/png,image/webp';
const MANAGE_BRANDING_PERMISSION = 'settings.manage_branding';

type BrandingAssetKind = 'logo' | 'logo_dark' | 'favicon' | 'pwa_icon';

interface BrandingUploadResponse {
  fileUrl: string;
  mimeType: string;
}

interface BrandingPresignResponse {
  uploadUrl: string;
  fileKey: string;
  fileUrl: string;
}

interface BrandingAssetsResponse {
  pwaIcons?: Array<{ src: string; sizes: string; type: string }>;
}

function normalizeMimeType(mimeType: string): string {
  return (mimeType.split(';')[0] ?? '').trim().toLowerCase();
}

function isAllowedBrandingFile(file: File): boolean {
  const mime = normalizeMimeType(file.type);
  return mime === 'image/png' || mime === 'image/webp';
}

async function uploadBrandingAsset(
  file: File,
  kind: BrandingAssetKind,
): Promise<BrandingUploadResponse> {
  const presignBody: {
    kind: BrandingAssetKind;
    mimeType: string;
    fileName: string;
    fileSizeBytes: number;
    sizes?: '512x512';
  } = {
    kind,
    mimeType: normalizeMimeType(file.type),
    fileName: file.name,
    fileSizeBytes: file.size,
  };
  if (kind === 'pwa_icon') {
    presignBody.sizes = '512x512';
  }

  const presign = await api.post<BrandingPresignResponse>(
    '/tenants/branding-assets/upload-url',
    presignBody,
  );

  const putResponse = await fetch(presign.uploadUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': normalizeMimeType(file.type) },
  });
  if (!putResponse.ok) {
    throw new Error(`Upload failed: ${putResponse.statusText}`);
  }

  const registerBody: {
    kind: BrandingAssetKind;
    fileKey: string;
    mimeType: string;
    sizes?: '512x512';
  } = {
    kind,
    fileKey: presign.fileKey,
    mimeType: normalizeMimeType(file.type),
  };
  if (kind === 'pwa_icon') {
    registerBody.sizes = '512x512';
  }

  return api.post<BrandingUploadResponse>('/tenants/branding-assets', registerBody);
}

interface HiddenFileButtonProps {
  id: string;
  label: string;
  disabled: boolean;
  onFile: (file: File) => void;
}

function HiddenFileButton({ id, label, disabled, onFile }: HiddenFileButtonProps) {
  return (
    <Button
      component="label"
      variant="outlined"
      size="small"
      startIcon={<CloudUploadIcon />}
      disabled={disabled}
    >
      {label}
      <input
        id={id}
        type="file"
        accept={BRANDING_FILE_ACCEPT}
        hidden
        disabled={disabled}
        aria-label={label}
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) onFile(file);
        }}
      />
    </Button>
  );
}

interface ColorFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

function ColorField({ label, value, onChange, disabled = false }: ColorFieldProps) {
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Box
        component="input"
        type="color"
        disabled={disabled}
        value={value || '#000000' /* design-allow-hex: native color input requires a literal hex fallback */}
        onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
        sx={{
          width: 40,
          height: 40,
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          cursor: 'pointer',
          p: 0.5,
          bgcolor: 'background.paper',
        }}
      />
      <TextField
        label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        size="small"
        placeholder="#000000" // design-allow-hex: hex-format hint shown to the user in the picker field
        error={!!value && !isValidColor(value)}
        disabled={disabled}
        sx={{ width: 160 }}
      />
    </Stack>
  );
}

interface ColorGroupProps {
  title: string;
  color: Partial<BrandingColorConfig>;
  onChange: (color: Partial<BrandingColorConfig>) => void;
  t: (key: string) => string;
  disabled?: boolean;
}

function ColorGroup({ title, color, onChange, t, disabled = false }: ColorGroupProps) {
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        {title}
      </Typography>
      <Stack spacing={2}>
        <ColorField
          label={t('branding.colorMain')}
          value={color.main ?? ''}
          disabled={disabled}
          onChange={(main) => onChange({ ...color, main })}
        />
        <Accordion disableGutters elevation={0} sx={{ '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0, minHeight: 'auto' }}>
            <Typography variant="body2" color="text.secondary">
              {t('branding.advancedColors')}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 0 }}>
            <Stack spacing={2}>
              <ColorField
                label={t('branding.colorLight')}
                value={color.light ?? ''}
                disabled={disabled}
                onChange={(light) => onChange({ ...color, light: light || undefined })}
              />
              <ColorField
                label={t('branding.colorDark')}
                value={color.dark ?? ''}
                disabled={disabled}
                onChange={(dark) => onChange({ ...color, dark: dark || undefined })}
              />
              <ColorField
                label={t('branding.colorContrast')}
                value={color.contrastText ?? ''}
                disabled={disabled}
                onChange={(contrastText) => onChange({ ...color, contrastText: contrastText || undefined })}
              />
            </Stack>
          </AccordionDetails>
        </Accordion>
      </Stack>
    </Paper>
  );
}

export function BrandingTab() {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const hasPermission = useAuthStore((s) => s.hasPermission);
  // Branding writes require settings.manage_branding server-side; keep the read view.
  const canManageBranding = hasPermission(MANAGE_BRANDING_PERMISSION);
  const { branding, tenantName, tenantSlug, support } = useBranding();
  const setBrandingStore = useBrandingStore((s) => s.setBranding);

  // Form state
  const [companyName, setCompanyName] = useState('');
  const [logo, setLogo] = useState<Partial<BrandingLogoConfig>>({});
  const [favicon, setFavicon] = useState('');
  const [pwaIconUrl, setPwaIconUrl] = useState('');
  const [primaryColor, setPrimaryColor] = useState<Partial<BrandingColorConfig>>({});
  const [secondaryColor, setSecondaryColor] = useState<Partial<BrandingColorConfig>>({});
  const [fontFamily, setFontFamily] = useState('');
  const [fontUrl, setFontUrl] = useState('');
  const [helpUrl, setHelpUrl] = useState('');
  const [supportEmail, setSupportEmail] = useState('');
  const [senderName, setSenderName] = useState('');
  const [replyTo, setReplyTo] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploadingKind, setUploadingKind] = useState<BrandingAssetKind | null>(null);
  const [previewBust, setPreviewBust] = useState<Partial<Record<BrandingAssetKind, number>>>({});

  // Initialize form from branding
  useEffect(() => {
    if (branding) {
      setCompanyName(branding.companyName ?? '');
      setLogo(branding.logo ?? {});
      setFavicon(branding.favicon ?? '');
      setPrimaryColor(branding.colors?.primary ?? {});
      setSecondaryColor(branding.colors?.secondary ?? {});
      setFontFamily(branding.typography?.fontFamily ?? '');
      setFontUrl(branding.typography?.fontUrl ?? '');
    }
  }, [branding]);

  // Initialize support fields
  useEffect(() => {
    if (support) {
      setHelpUrl(support.helpUrl ?? '');
      setSupportEmail(support.supportEmail ?? '');
      setSenderName(support.senderName ?? '');
      setReplyTo(support.replyTo ?? '');
    }
  }, [support]);

  useEffect(() => {
    let cancelled = false;
    void api
      .get<BrandingAssetsResponse>('/tenants/branding-assets')
      .then((data) => {
        const src = data.pwaIcons?.[0]?.src;
        if (!cancelled && src) setPwaIconUrl(src);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const buildPayload = useCallback((): Partial<BrandingConfig> => {
    const payload: Partial<BrandingConfig> = {};

    if (companyName) payload.companyName = companyName;

    // Logo — the light logo carries the slot, so sizes and a dark variant
    // only persist alongside it.
    if (logo.url) {
      payload.logo = {
        url: logo.url,
        ...(logo.darkUrl && { darkUrl: logo.darkUrl }),
        ...(logo.height && { height: logo.height }),
        ...(logo.width && { width: logo.width }),
      };
    }

    if (favicon) payload.favicon = favicon;

    // Colors — only include if main is set
    const hasColors = primaryColor.main || secondaryColor.main;
    if (hasColors) {
      payload.colors = {};
      if (primaryColor.main) {
        payload.colors = { ...payload.colors, primary: primaryColor as BrandingColorConfig };
      }
      if (secondaryColor.main) {
        payload.colors = { ...payload.colors, secondary: secondaryColor as BrandingColorConfig };
      }
    }

    // Typography
    if (fontFamily || fontUrl) {
      payload.typography = {
        ...(fontFamily && { fontFamily }),
        ...(fontUrl && { fontUrl }),
      };
    }

    return payload;
  }, [companyName, logo, favicon, primaryColor, secondaryColor, fontFamily, fontUrl]);

  const handleAssetFile = async (kind: BrandingAssetKind, file: File) => {
    if (!hasPermission(MANAGE_BRANDING_PERMISSION)) return;
    if (!isAllowedBrandingFile(file)) {
      notify.error(tc('error'), tc('branding.unsupportedType'));
      return;
    }

    setUploadingKind(kind);
    try {
      const result = await uploadBrandingAsset(file, kind);
      // Every upload of a slot answers on the same proxy URL, so the preview
      // would keep showing the previous image from cache. The saved value
      // stays clean — only what this page renders carries the buster.
      setPreviewBust((prev) => ({ ...prev, [kind]: Date.now() }));
      if (kind === 'logo') {
        setLogo((prev) => ({ ...prev, url: result.fileUrl }));
      } else if (kind === 'logo_dark') {
        setLogo((prev) => ({ ...prev, darkUrl: result.fileUrl }));
      } else if (kind === 'favicon') {
        setFavicon(result.fileUrl);
      } else {
        setPwaIconUrl(result.fileUrl);
      }
      notify.success(tc('branding.uploadComplete'), tc('branding.uploadCompleteDesc'));
    } catch (err) {
      console.error('Branding asset upload failed:', err);
      notify.error(tc('error'), tc('branding.uploadFailed'));
    } finally {
      setUploadingKind(null);
    }
  };

  const handleSave = async () => {
    if (!hasPermission(MANAGE_BRANDING_PERMISSION)) return;
    setSaving(true);
    try {
      const payload = buildPayload();

      // Save branding
      await api.patch('/tenants/me/branding', payload);

      // Save support settings via settings endpoint
      const supportPayload = {
        support: {
          ...(helpUrl && { helpUrl }),
          ...(supportEmail && { supportEmail }),
          ...(senderName && { senderName }),
          ...(replyTo && { replyTo }),
        },
      };
      await api.patch('/tenants/me/settings', supportPayload);

      // Update store so theme/logo/favicon update immediately
      const updatedResponse: TenantBrandingResponse = {
        tenantName: tenantName ?? '',
        tenantSlug: tenantSlug ?? '',
        branding: payload as BrandingConfig,
        support: Object.keys(supportPayload.support).length > 0
          ? supportPayload.support
          : null,
      };
      setBrandingStore(updatedResponse, tenantName ?? '');

      notify.success(t('branding.saved'), t('branding.savedDesc'));
    } catch (err) {
      console.error('Branding save failed:', err);
      notify.error(tc('error'), describeApiError(err, tc, { fallback: t('branding.saveFailed') }));
    } finally {
      setSaving(false);
    }
  };

  const uploadsDisabled = !canManageBranding || saving || uploadingKind !== null;

  /** Render-only URL: appends this session's cache buster for the slot. */
  const previewSrc = (url: string, kind: BrandingAssetKind): string => {
    const bust = previewBust[kind];
    if (!bust) return url;
    return `${url}${url.includes('?') ? '&' : '?'}v=${bust}`;
  };

  return (
    <Box>
      <Typography variant="h6" fontWeight={600} gutterBottom>
        {t('branding.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('branding.subtitle')}
      </Typography>

      <Stack spacing={3}>
        {/* Company Name */}
        <Paper variant="outlined" sx={{ p: 2.5 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            {t('branding.companyName')}
          </Typography>
          <TextField
            fullWidth
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            helperText={t('branding.companyNameHint')}
            size="small"
            disabled={!canManageBranding}
          />
        </Paper>

        {/* Logo */}
        <Paper variant="outlined" sx={{ p: 2.5 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            {t('branding.logo')}
          </Typography>

          {/* Logo preview */}
          <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1, textAlign: 'center' }}>
            {logo.url ? (
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  {t('branding.currentLogo')}
                </Typography>
                <Box
                  component="img"
                  src={previewSrc(logo.url, 'logo')}
                  alt={tc('branding.logoAlt')}
                  sx={{
                    maxHeight: logo.height ?? 40,
                    maxWidth: logo.width ?? 200,
                    objectFit: 'contain',
                  }}
                  onError={(e: SyntheticEvent<HTMLImageElement>) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t('branding.noLogo')}
              </Typography>
            )}
          </Box>

          <Stack spacing={2}>
            {canManageBranding && (
              <Typography variant="body2" color="text.secondary">
                {tc('branding.uploadHint')}
              </Typography>
            )}
            {canManageBranding && (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <HiddenFileButton
                  id="branding-upload-logo"
                  label={uploadingKind === 'logo' ? tc('branding.uploading') : tc('branding.uploadLogo')}
                  disabled={uploadsDisabled}
                  onFile={(file) => void handleAssetFile('logo', file)}
                />
                <HiddenFileButton
                  id="branding-upload-logo-dark"
                  label={
                    uploadingKind === 'logo_dark'
                      ? tc('branding.uploading')
                      : tc('branding.uploadDarkLogo')
                  }
                  disabled={uploadsDisabled}
                  onFile={(file) => void handleAssetFile('logo_dark', file)}
                />
              </Stack>
            )}
            <Stack direction="row" spacing={2}>
              <TextField
                label={t('branding.logoHeight')}
                type="number"
                value={logo.height ?? ''}
                onChange={(e) => setLogo({ ...logo, height: e.target.value ? Number(e.target.value) : undefined })}
                size="small"
                disabled={!canManageBranding}
                sx={{ width: 140 }}
              />
              <TextField
                label={t('branding.logoWidth')}
                type="number"
                value={logo.width ?? ''}
                onChange={(e) => setLogo({ ...logo, width: e.target.value ? Number(e.target.value) : undefined })}
                size="small"
                disabled={!canManageBranding}
                sx={{ width: 140 }}
              />
            </Stack>

            <Typography variant="subtitle2">{t('branding.favicon')}</Typography>
            {favicon ? (
              <Box
                component="img"
                src={previewSrc(favicon, 'favicon')}
                alt={tc('branding.faviconAlt')}
                sx={{ width: 32, height: 32, objectFit: 'contain' }}
              />
            ) : null}
            {canManageBranding && (
              <HiddenFileButton
                id="branding-upload-favicon"
                label={
                  uploadingKind === 'favicon' ? tc('branding.uploading') : tc('branding.uploadFavicon')
                }
                disabled={uploadsDisabled}
                onFile={(file) => void handleAssetFile('favicon', file)}
              />
            )}

            <Typography variant="subtitle2">{tc('branding.pwaIcons')}</Typography>
            {canManageBranding && (
              <Typography variant="body2" color="text.secondary">
                {tc('branding.pwaIconsHint')}
              </Typography>
            )}
            {pwaIconUrl ? (
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  {tc('branding.currentPwaIcon')}
                </Typography>
                <Box
                  component="img"
                  src={previewSrc(pwaIconUrl, 'pwa_icon')}
                  alt={tc('branding.pwaIconAlt')}
                  sx={{ width: 64, height: 64, objectFit: 'contain' }}
                />
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {tc('branding.noPwaIcon')}
              </Typography>
            )}
            {canManageBranding && (
              <HiddenFileButton
                id="branding-upload-pwa-icon"
                label={
                  uploadingKind === 'pwa_icon' ? tc('branding.uploading') : tc('branding.uploadPwaIcon')
                }
                disabled={uploadsDisabled}
                onFile={(file) => void handleAssetFile('pwa_icon', file)}
              />
            )}
          </Stack>
        </Paper>

        {/* Colors */}
        <Paper variant="outlined" sx={{ p: 2.5 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            {t('branding.colors')}
          </Typography>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
            <Box sx={{ flex: 1 }}>
              <ColorGroup
                title={t('branding.primaryColor')}
                color={primaryColor}
                onChange={setPrimaryColor}
                t={t}
                disabled={!canManageBranding}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <ColorGroup
                title={t('branding.secondaryColor')}
                color={secondaryColor}
                onChange={setSecondaryColor}
                t={t}
                disabled={!canManageBranding}
              />
            </Box>
          </Stack>

          {/* Live preview */}
          {(primaryColor.main || secondaryColor.main) && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                {t('branding.preview')}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {primaryColor.main && (
                  <>
                    <Button
                      variant="contained"
                      size="small"
                      sx={{
                        bgcolor: primaryColor.main,
                        color: primaryColor.contrastText ?? 'common.white',
                        '&:hover': { bgcolor: primaryColor.dark ?? primaryColor.main },
                      }}
                    >
                      {t('branding.primaryColor')}
                    </Button>
                    <Chip
                      label={t('branding.primaryColor')}
                      size="small"
                      sx={{ bgcolor: primaryColor.light ?? primaryColor.main, color: primaryColor.contrastText ?? 'common.white' }}
                    />
                  </>
                )}
                {secondaryColor.main && (
                  <>
                    <Button
                      variant="contained"
                      size="small"
                      sx={{
                        bgcolor: secondaryColor.main,
                        color: secondaryColor.contrastText ?? 'common.white',
                        '&:hover': { bgcolor: secondaryColor.dark ?? secondaryColor.main },
                      }}
                    >
                      {t('branding.secondaryColor')}
                    </Button>
                    <Chip
                      label={t('branding.secondaryColor')}
                      size="small"
                      sx={{ bgcolor: secondaryColor.light ?? secondaryColor.main, color: secondaryColor.contrastText ?? 'common.white' }}
                    />
                  </>
                )}
              </Stack>
            </Box>
          )}
        </Paper>

        {/* Typography */}
        <Paper variant="outlined" sx={{ p: 2.5 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            {t('branding.typography')}
          </Typography>
          <Stack spacing={2}>
            <TextField
              fullWidth
              label={t('branding.fontFamily')}
              value={fontFamily}
              onChange={(e) => setFontFamily(e.target.value)}
              helperText={t('branding.fontFamilyHint')}
              size="small"
              disabled={!canManageBranding}
            />
            <TextField
              fullWidth
              label={t('branding.fontUrl')}
              value={fontUrl}
              onChange={(e) => setFontUrl(e.target.value)}
              helperText={t('branding.fontUrlHint')}
              size="small"
              disabled={!canManageBranding}
            />
          </Stack>
        </Paper>

        {/* Support / Help links */}
        <Paper variant="outlined" sx={{ p: 2.5 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            {t('branding.support')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('branding.supportHint')}
          </Typography>
          <Stack spacing={2}>
            <TextField
              fullWidth
              label={t('branding.helpUrl')}
              value={helpUrl}
              onChange={(e) => setHelpUrl(e.target.value)}
              helperText={t('branding.helpUrlHint')}
              size="small"
              type="url"
              disabled={!canManageBranding}
            />
            <TextField
              fullWidth
              label={t('branding.supportEmail')}
              value={supportEmail}
              onChange={(e) => setSupportEmail(e.target.value)}
              helperText={t('branding.supportEmailHint')}
              size="small"
              type="email"
              disabled={!canManageBranding}
            />
          </Stack>
        </Paper>

        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            {t('branding.customerEmail')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('branding.customerEmailHint')}
          </Typography>
          <Stack spacing={2}>
            <TextField
              fullWidth
              label={t('branding.senderName')}
              value={senderName}
              onChange={(e) => setSenderName(e.target.value)}
              helperText={t('branding.senderNameHint', { name: companyName || tenantName || '' })}
              size="small"
              slotProps={{ htmlInput: { maxLength: 120 } }}
              disabled={!canManageBranding}
            />
            <TextField
              fullWidth
              label={t('branding.replyTo')}
              value={replyTo}
              onChange={(e) => setReplyTo(e.target.value)}
              helperText={t('branding.replyToHint')}
              size="small"
              type="email"
              disabled={!canManageBranding}
            />
          </Stack>
        </Paper>

        {canManageBranding && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={() => void handleSave()}
              disabled={saving || uploadingKind !== null}
            >
              {saving ? t('saving') : t('saveChanges')}
            </Button>
          </Box>
        )}

        {/* Own buttons: domain changes go to Resend, not through "Save changes". */}
        <EmailDomainSection canManage={canManageBranding} />
      </Stack>
    </Box>
  );
}
